import { Trans, useTranslation } from 'react-i18next';
import { Box, Grid, Typography } from '@mui/material';
import { ClassNameMap, withStyles } from '@mui/styles';
import 'moment/locale/fr';
import 'moment/locale/es';

import { SessionStyle } from './styles/styles';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import unplugCharger from '../../../assets/images/charger/charger-time-to-unplug.svg';
import { ChargerDtoV2, CostBreakdownItem, IdleFeesStatus, Session } from '../dto/charger.dto';
import {
  formatPrice,
  getRateCountWithoutType,
  getTimeinAMPMFormat,
} from '../charger-pricing/utils';
import { PricingType } from '../../../stores/types/price.interface';
import { useMemo } from 'react';
interface IProps {
  classes: ClassNameMap;
  session: Session;
  charger: ChargerDtoV2;
  lastCostItem: CostBreakdownItem;
}
const ChargerIdling = ({ classes, session, charger, lastCostItem: _lastCostItem }: IProps) => {
  const { i18n } = useTranslation();

  const chargerHasNoIdleFees = useMemo(
    () => charger.currentPrice.idleRate === 0,
    [charger.currentPrice.idleRate],
  );

  const isChargerInEffectiveIdling = useMemo(
    () =>
      session.idleFeeStatus === IdleFeesStatus.IMPOSED ||
      session.idleFeeStatus === IdleFeesStatus.GRACE_PERIOD,
    [session.idleFeeStatus],
  );

  const isFreeAndPublic = useMemo(() => {
    return charger.owner.type === 'COMPANY' && charger.currentPrice.pricingType === 'FREE';
  }, [charger.owner.type, charger.currentPrice.pricingType]);

  /** True when backend reports imposed idle fees and the tariff actually bills idle time. */
  const showIdleFeesImposedCopy = useMemo(
    () => session.idleFeeStatus === IdleFeesStatus.IMPOSED && !chargerHasNoIdleFees,
    [session.idleFeeStatus, chargerHasNoIdleFees],
  );

  const isTimePluggedIn = useMemo(() => {
    return charger.currentPrice.pricingType === PricingType.BILLED_BY_TIME_PLUGGED_IN;
  }, [charger.currentPrice.pricingType]);

  const isTimePluggedInWithoutIdleFeesApplied = useMemo(
    () => (chargerHasNoIdleFees || !isChargerInEffectiveIdling) && isTimePluggedIn,
    [chargerHasNoIdleFees, isTimePluggedIn, isChargerInEffectiveIdling],
  );

  const sessionPluggedInRateLabel = useMemo(() => {
    return formatPrice(charger.currentPrice.unitPrice, charger.currentPrice.currency);
  }, [charger.currentPrice.unitPrice, charger.currentPrice.currency]);

  const isInIdleGracePeriodWithIdleFees = useMemo(() => {
    return session.idleFeeStatus === IdleFeesStatus.GRACE_PERIOD && !chargerHasNoIdleFees;
  }, [session.idleFeeStatus, chargerHasNoIdleFees]);

  const subheadingKey = useMemo(() => {
    if (showIdleFeesImposedCopy) {
      return 'idle_fees_imposed_subheading';
    }
    if (isTimePluggedIn) {
      return 'time_to_move_vehicle_subheading_billed_until_unplug';
    }
    if (isInIdleGracePeriodWithIdleFees) {
      return 'idle_grace_period_subheading';
    }
    return 'no_idle_fee_no_plugin_subheading';
  }, [showIdleFeesImposedCopy, isTimePluggedIn, isInIdleGracePeriodWithIdleFees]);

  return (
    <Box
      style={{
        padding: '16px 12px 16px 12px',
      }}
    >
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 20,
          marginBottom: 20,
        }}
      >
        <Typography component='div' className={classes.idlingMainHeading}>
          {showIdleFeesImposedCopy ? (
            i18n.t('idle_fees_imposed_heading')
          ) : (
            <>
              {i18n.t('time_to_unplug_line1')}
              <br />
              {i18n.t('time_to_unplug_line2')}
            </>
          )}
        </Typography>
        <Typography component='p' className={classes.idlingSubheading}>
          {i18n.t(subheadingKey)}
        </Typography>
      </Box>
      <Grid container item justifyContent='center'>
        <img src={unplugCharger} alt={i18n.t('time_to_unplug_illustration_alt')} />
      </Grid>
      {isTimePluggedInWithoutIdleFeesApplied && (
        <Box className={classes.idlingWarningPanel} style={{ marginTop: 20 }}>
          <Box className={classes.idlingWarningTitleRow}>
            <WarningRoundedIcon style={{ color: '#FF8F00', fontSize: 24, flexShrink: 0 }} />
            <Typography className={classes.message2} component='div'>
              <Trans
                i18nKey='plugged_in_no_idle_warning'
                values={{ cost: sessionPluggedInRateLabel }}
                components={{ b: <strong /> }}
              />
            </Typography>
          </Box>
        </Box>
      )}
      {!chargerHasNoIdleFees && isChargerInEffectiveIdling && (
        <Box className={classes.idlingWarningPanel} style={{ marginTop: 20 }}>
          {session.idleFeeStatus === IdleFeesStatus.GRACE_PERIOD ? (
            <>
              <Box className={classes.idlingWarningTitleRow}>
                <WarningRoundedIcon style={{ color: '#FF8F00', fontSize: 24, flexShrink: 0 }} />
                <Typography className={classes.subHeader} style={{ padding: 0 }}>
                  {charger.currentPrice.pricingType === PricingType.BILLED_BY_TIME_PLUGGED_IN
                    ? i18n.t('idle_fees_grace_panel_title')
                    : i18n.t('idle_fees_start', {
                        time: getTimeinAMPMFormat(session.idleFeeStartTime),
                      })}
                </Typography>
              </Box>

              {charger.currentPrice.pricingType === PricingType.BILLED_BY_TIME_PLUGGED_IN ? (
                <>
                  <Typography className={classes.message2} component='div'>
                    <Trans
                      i18nKey='plugged_in_no_idle_warning'
                      values={{ cost: sessionPluggedInRateLabel }}
                      components={{ b: <strong /> }}
                    />
                  </Typography>
                  <Typography className={classes.message2} component='div' style={{ marginTop: 8 }}>
                    <Trans
                      i18nKey='idle_fees_start_plugged_in_warning'
                      values={{
                        cost: getRateCountWithoutType(
                          charger.currentPrice.idleRate,
                          charger.currentPrice.currency,
                        ),
                        time: getTimeinAMPMFormat(session.idleFeeStartTime),
                      }}
                      components={{ b: <strong /> }}
                    />
                  </Typography>
                </>
              ) : (
                <>
                  <Typography className={classes.message2} component='div' style={{ marginTop: 8 }}>
                    <span>
                      {i18n.t('idle_fees_start_warning', {
                        cost: getRateCountWithoutType(
                          charger.currentPrice.idleRate,
                          charger.currentPrice.currency,
                        ),
                        time: getTimeinAMPMFormat(session.idleFeeStartTime),
                      })}
                    </span>
                  </Typography>
                </>
              )}
            </>
          ) : (
            <Box className={classes.idlingWarningTitleRow}>
              <WarningRoundedIcon style={{ color: '#FF8F00', fontSize: 24, flexShrink: 0 }} />
              <Typography className={classes.message2}>
                <span className={classes.warning}>
                  {i18n.t('idle_fees_active_message', {
                    cost: getRateCountWithoutType(
                      charger.currentPrice.idleRate,
                      charger.currentPrice.currency,
                    ),
                  })}
                </span>{' '}
                <span>{i18n.t('idle_fees_active_end')}</span>
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default withStyles(SessionStyle)(ChargerIdling);
